import { z } from "zod";

export const CUSTOMER_EVIDENCE_FLAG = "customer_evidence_import";
export const CUSTOMER_EVIDENCE_FIELDS = [
  "jobTitle", "seniority", "jobFunction", "department", "industry", "subIndustry",
  "employeeRange", "revenueRange", "companyName", "companyState", "companyCountry",
  "businessModel", "matchStatus", "personMoved",
] as const;
export type EvidenceField = typeof CUSTOMER_EVIDENCE_FIELDS[number];
export type EvidenceMapping = Partial<Record<EvidenceField, number>>;
export const evidenceMappingSchema = z.object(Object.fromEntries(
  CUSTOMER_EVIDENCE_FIELDS.map((key) => [key, z.number().int().min(0).max(255).optional()]),
) as Record<EvidenceField, z.ZodOptional<z.ZodNumber>>).strict();
export const ZOOMINFO_HEADERS: Partial<Record<EvidenceField, string>> = {
  jobTitle: "Job Title", seniority: "Management Level", jobFunction: "Job Function",
  department: "Department", industry: "Primary Industry", subIndustry: "Primary Sub-Industry",
  employeeRange: "Employee Range", revenueRange: "Revenue Range (in USD)",
  companyName: "Company Name", companyState: "Company State", companyCountry: "Company Country",
  businessModel: "Business Model", matchStatus: "Match status", personMoved: "Person Has Moved",
};
export interface EvidenceCount { label: string; count: number; pct: number; denominator: number }
export interface CustomerEvidenceAggregate {
  source: "zoominfo" | "custom";
  window: string;
  rows: number;
  usable_rows: number;
  companies: number;
  company_fields_basis?: "eligible_rows";
  denominators: { person: number; title: number; company: number; company_fields?: number };
  missing: Record<string, number>;
  business_model: Record<string, number>;
  title_buckets: (EvidenceCount & { examples: string[] })[];
  top_titles: { title: string; count: number }[];
  seniority: Record<string, number>;
  industries: EvidenceCount[];
  company_size: (EvidenceCount & { companies: number })[];
  geography: { country: Record<string, number>; top_states: EvidenceCount[] };
  optional: Record<string, unknown>;
  caveats: string[];
}
export interface CustomerEvidenceImport {
  id: string; brandId: string; label: string; sourcePreset: string;
  columnMapping: EvidenceMapping; rowCount: number; usableRowCount: number;
  aggregate: CustomerEvidenceAggregate; status: "active" | "archived";
  importedAt: string; importedBy: string;
}

// Project historical aggregates without rewriting stored imports or snapshots.
export function personaEvidenceSummary(aggregate: CustomerEvidenceAggregate): CustomerEvidenceAggregate {
  const { person, title, company, company_fields } = aggregate.denominators;
  return {
    source: aggregate.source,
    window: aggregate.window,
    rows: aggregate.rows,
    usable_rows: aggregate.usable_rows,
    companies: aggregate.companies,
    ...(aggregate.company_fields_basis ? { company_fields_basis: aggregate.company_fields_basis } : {}),
    denominators: { person, title, company, ...(company_fields === undefined ? {} : { company_fields }) },
    missing: Object.fromEntries(Object.entries(aggregate.missing).filter(([field]) => (CUSTOMER_EVIDENCE_FIELDS as readonly string[]).includes(field))),
    business_model: aggregate.business_model,
    title_buckets: aggregate.title_buckets,
    top_titles: aggregate.top_titles,
    seniority: aggregate.seniority,
    industries: aggregate.industries,
    company_size: aggregate.company_size,
    geography: aggregate.geography,
    optional: Object.fromEntries(Object.entries(aggregate.optional ?? {}).filter(([section]) =>
      ["function", "department", "subindustries", "revenue_range"].includes(section))),
    caveats: aggregate.caveats,
  };
}
